// Fiche d'une demande d'urbanisme (lot L5-C2, M18) : dossier M05 (numéro, état, lien vers la fiche du dossier
// où se jouent complément, transmission, décision, notification et clôture), type et autorité configurés,
// localisation et zone, conditions de la décision, avis datés des services, inspections M23 liées, historique
// fusionné. Les actes proposés suivent les droits ; le serveur refait tous les contrôles.
import { Bandeau, Bouton, Lien, LienBouton, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import type { AvisUrbanisme, InspectionLiee } from '@cc/contrats';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useDemande } from './api';
import { FormulaireInspection, FormulaireLocalisation, FormulaireRenduAvis, FormulaireSollicitation } from './FormulairesUrbanisme';
import { libelleAvancement, libelleSens, texteConditions, TON_AVANCEMENT, TON_SENS } from './logique';
import { dateCourte } from './PageDemandes';
import styles from '../achats/achats.module.css';

type Dialogue = 'localisation' | 'avis' | 'inspection' | { rendu: string } | null;
const heure = (iso: string) => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Libreville' });

export function PageDemande() {
  const { id } = useParams();
  const { contexte } = useSession();
  const demande = useDemande(id);
  const [d, setD] = useState<Dialogue>(null);
  const droits = { modifier: peut(contexte, 'urbanisme', 'modifier'), valider: peut(contexte, 'urbanisme', 'valider') };
  const x = demande.data;
  const ouverte = x?.avancement === 'en_cours';
  const colonnesAvis: ColonneTableau<AvisUrbanisme>[] = [
    { id: 'service', entete: 'Service', enTeteLigne: true, masquable: false, cellule: (a) => `${a.service.nom}${a.requis ? ' (requis)' : ''}` },
    { id: 'sollicite', entete: 'Sollicité le', cellule: (a) => heure(a.sollicite_le), valeurTri: (a) => a.sollicite_le },
    { id: 'echeance', entete: 'Échéance', cellule: (a) => a.echeance },
    { id: 'sens', entete: 'Avis', cellule: (a) => (a.sens ? <PuceEtat ton={TON_SENS[a.sens]} libelle={libelleSens(a.sens)} /> : <PuceEtat ton="neutre" libelle="En attente" />) },
    { id: 'date', entete: 'Date de l\'avis', cellule: (a) => (a.date_avis ? `${a.date_avis}${a.dans_delai === false ? ' (hors délai)' : ''}` : 'non rendu') },
    { id: 'observations', entete: 'Observations', cellule: (a) => a.observations ?? a.objet ?? '' },
    { id: 'acte', entete: 'Acte', cellule: (a) => (a.etat === 'sollicite' && ouverte && droits.valider ? <Bouton variante="secondaire" onClick={() => setD({ rendu: a.id })}>Rendre l&apos;avis</Bouton> : a.rendu_par ?? '') },
  ];
  const colonnesInspections: ColonneTableau<InspectionLiee>[] = [
    { id: 'ordre', entete: 'Ordre de mission', enTeteLigne: true, masquable: false, cellule: (i) => <Lien href={`/inspections/ordres/${i.ordre.id}`} className="cc-mono">{i.ordre.numero}</Lien> },
    { id: 'periode', entete: 'Période', cellule: (i) => `${i.ordre.periode_debut} au ${i.ordre.periode_fin}` },
    { id: 'etat', entete: 'État', cellule: (i) => i.ordre.etat },
    { id: 'constats', entete: 'Constats', numerique: true, cellule: (i) => String(i.ordre.constats) },
    { id: 'conclusion', entete: 'Conclusion', cellule: (i) => i.ordre.conclusion ?? 'aucun constat' },
    { id: 'motif', entete: 'Motif', cellule: (i) => i.motif },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Urbanisme"
        titre={x ? `${x.numero ?? 'Demande'} : ${x.type.libelle}` : 'Demande d\'urbanisme'}
        description={x ? `${x.demandeur ?? 'Demandeur non renseigné'}, enregistrée le ${dateCourte(x.enregistre_le)}.` : undefined}
        actions={<Lien href="/urbanisme">Toutes les demandes</Lien>}
      />
      <EtatRequete chargement={demande.isPending} erreur={demande.error} onReessayer={() => void demande.refetch()} lot="L5-C2" fonction="La fiche de la demande">
        {x ? (
          <>
            <div className={styles.actes}>
              <PuceEtat ton={TON_AVANCEMENT[x.avancement]} libelle={libelleAvancement(x.avancement)} />
              <LienBouton href={`/dossiers/${x.dossier_id}`} variante="principal">Ouvrir le dossier (instruction et décision)</LienBouton>
              {ouverte && droits.modifier ? <Bouton variante="secondaire" onClick={() => setD('localisation')}>Localiser</Bouton> : null}
              {ouverte && droits.modifier ? <Bouton variante="secondaire" onClick={() => setD('avis')}>Solliciter un avis</Bouton> : null}
              {ouverte && droits.modifier ? <Bouton variante="secondaire" onClick={() => setD('inspection')}>Lier une inspection</Bouton> : null}
            </div>
            <section className={styles.bloc} aria-labelledby="titre-type">
              <h2 id="titre-type">Demande et configuration</h2>
              <dl className={styles.fiche}>
                <dt>État du dossier</dt><dd>{x.etat_dossier}{x.resultat ? `, décision : ${x.resultat === 'approuve' ? 'accordée' : x.resultat === 'refuse' ? 'refusée' : x.resultat}` : ''}</dd>
                <dt>Compétence</dt><dd>{`${x.type_detail.competence}${x.type_detail.fictif ? ' (fictif)' : ''}`}</dd>
                <dt>Source</dt><dd>{x.type_detail.reference_acte}</dd>
                <dt>Démarche</dt><dd>{x.type_detail.demarche.libelle}</dd>
                <dt>Zone</dt><dd>{x.zone ? `${x.zone.code} · ${x.zone.libelle}` : x.position ? 'Hors des zones en vigueur' : 'Demande non localisée'}</dd>
                <dt>Position</dt><dd className="cc-mono">{x.position ? `${x.position.longitude.toFixed(5)} ${x.position.latitude.toFixed(5)}` : 'non renseignée'}</dd>
              </dl>
            </section>
            <section className={styles.bloc} aria-labelledby="titre-conditions">
              <h2 id="titre-conditions">Conditions de la décision</h2>
              {ouverte ? (
                <Bandeau type={x.conditions.avis_manquants.length === 0 && !x.conditions.inspection_manquante ? 'info' : 'attention'}>
                  <ul className={styles.detail}>{texteConditions(x.conditions).map((t) => <li key={t}>{t}</li>)}</ul>
                </Bandeau>
              ) : <p className={styles.detail}>La demande est {libelleAvancement(x.avancement).toLowerCase()} ; aucun avis ni inspection nouveau.</p>}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-avis">
              <h2 id="titre-avis">Avis des services</h2>
              {x.avis_detail.length === 0 ? <p className={styles.detail}>Aucun avis sollicité.</p> : <Tableau legende="Avis des services" colonnes={colonnesAvis} lignes={x.avis_detail} cleLigne={(a) => a.id} cleColonnes="urbanisme-avis" />}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-inspections">
              <h2 id="titre-inspections">Inspections</h2>
              {x.inspections_detail.length === 0 ? <p className={styles.detail}>{x.type_detail.inspection_requise ? 'Inspection exigée par ce type de demande : aucune liée.' : 'Aucune inspection liée.'}</p> : <Tableau legende="Inspections liées" colonnes={colonnesInspections} lignes={x.inspections_detail} cleLigne={(i) => i.id} cleColonnes="urbanisme-inspections" />}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-historique">
              <h2 id="titre-historique">Historique</h2>
              <ol className={styles.liste}>
                {x.historique.map((h, i) => (
                  <li key={`${h.horodatage}-${i}`}>
                    <span className="cc-mono">{heure(h.horodatage)}</span> {h.libelle}{h.auteur ? `, par ${h.auteur}` : ''}{h.motif ? ` : ${h.motif}` : ''}
                  </li>
                ))}
              </ol>
            </section>
            {d === 'localisation' ? <FormulaireLocalisation demande={x} onFermer={() => setD(null)} /> : null}
            {d === 'avis' ? <FormulaireSollicitation demande={x} onFermer={() => setD(null)} /> : null}
            {d === 'inspection' ? <FormulaireInspection demande={x} onFermer={() => setD(null)} /> : null}
            {d && typeof d === 'object' ? <FormulaireRenduAvis demande={x} avisId={d.rendu} onFermer={() => setD(null)} /> : null}
          </>
        ) : null}
      </EtatRequete>
    </>
  );
}
